import React, { useState, useEffect } from "react";
import { Mail, Lock, ArrowLeft, Sun, Moon, Loader2, Eye, EyeOff, ShieldCheck, ChevronRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

// ─── Design matches site palette exactly ─────────────────────────────────────
const TRUST = [
  { icon: "🔒", label: "256-bit encrypted" },
  { icon: "✅", label: "PPCA verified platform" },
  { icon: "🛡️", label: "Your data stays private" },
];

export default function Login() {
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [showPw, setShowPw]       = useState(false);
  const [apiError, setApiError]   = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [theme, setTheme]         = useState(
    () => (typeof window !== "undefined" ? localStorage.getItem("theme") || "light" : "light")
  );

  const navigate  = useNavigate();
  const location  = useLocation();
  const params    = new URLSearchParams(location.search);
  const redirectTo = params.get("redirect") || "/user-dashboard";
  const reason     = params.get("reason");
  const D          = theme === "dark";

  // If already logged in as a pilot, redirect to dashboard
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.role === "driver" || user.role !== "company") {
          navigate(redirectTo, { replace: true });
        }
      } catch (e) {
        // invalid JSON, ignore
      }
    }
  }, [navigate, redirectTo]);

  useEffect(() => {
    const root = window.document.documentElement;
    D ? root.classList.add("dark") : root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme, D]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!email || !password) { setApiError("Please enter your email and password."); return; }
    
    setIsLoading(true);
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
      // 🔒 SECURITY FIX: Pointing specifically to the pilot login endpoint
      const res  = await fetch(`${apiUrl}/api/pilot/login/`, {
        method: "POST", 
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      
      const data = await res.json();
      
      if (res.ok) {
        // Securely store the user object and tokens
        localStorage.setItem("user", JSON.stringify(data.user));
        
        if (data.token) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user_token", data.token); // Fallback for other components
          localStorage.setItem("access_token", data.token); // Fallback for other components
        }
        
        navigate(redirectTo, { replace: true });
      } else {
        setApiError(data.message || "Incorrect email or password. Please try again.");
      }
    } catch {
      setApiError("Connection error — please check your internet and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Token shortcuts ──────────────────────────────────────────────────────
  const bg        = D ? "#0d1f3c"               : "#f8fafc";
  const panelBg   = D ? "#142a4d"               : "#ffffff";
  const border    = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text      = D ? "#f0f4f8"               : "#0f172a";
  const textMid   = D ? "rgba(240,244,248,0.75)": "#475569";
  const textSoft  = D ? "rgba(240,244,248,0.50)": "#94a3b8";
  const inputBg   = D ? "rgba(255,255,255,0.06)": "#f8fafc";
  const inputFocus= D ? "rgba(251,191,36,0.50)" : "#f59e0b";
  const amber     = "#f59e0b";
  const amberD    = "#fbbf24";
  const amberBtn  = D ? amberD : amber;
  const sideBg    = D
    ? "linear-gradient(160deg, #0d1f3c 0%, #1a3358 55%, #0f2d56 100%)"
    : "linear-gradient(160deg, #0d1f3c 0%, #1a3358 100%)";

  const inputClass = {
    width: "100%", padding: "12px 12px 12px 42px",
    borderRadius: 12, border: `1.5px solid ${border}`,
    background: inputBg, color: text, fontSize: 14,
    outline: "none", fontFamily: "inherit", transition: "border-color .15s",
    boxSizing: "border-box",
  };

  return (
    <div style={{ minHeight: "100vh", display: "flex", fontFamily: "'Inter',sans-serif", background: bg, transition: "background .3s" }}>

      {/* ── LEFT PANEL — decorative / brand (hidden on mobile) ── */}
      <div style={{
        display: "none", 
        flex: "0 0 420px", maxWidth: 420, background: sideBg,
        flexDirection: "column", padding: "48px 44px",
        position: "relative", overflow: "hidden",
      }}
        className="hidden lg:flex flex-col"
      >
        <div aria-hidden style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.07) 1px,transparent 1px)", backgroundSize:"24px 24px", pointerEvents:"none" }} />
        <div aria-hidden style={{ position:"absolute", top:"-10%", left:"-10%", width:320, height:320, borderRadius:"50%", background:"radial-gradient(circle,rgba(251,191,36,0.18) 0%,transparent 70%)", pointerEvents:"none" }} />
        <div aria-hidden style={{ position:"absolute", bottom:"-8%", right:"-15%", width:360, height:360, borderRadius:"50%", background:"radial-gradient(circle,rgba(59,130,246,0.15) 0%,transparent 70%)", pointerEvents:"none" }} />

        <Link to="/" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:10, marginBottom:"auto" }}>
          <div style={{ width:42, height:42, borderRadius:10, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:20, color:"#0d1f3c", flexShrink:0 }}>P</div>
          <span style={{ fontSize:20, fontWeight:800, color:"#fff" }}>Pilot<span style={{ color:amberD }}>Link</span> <span style={{ fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.5)" }}>USA</span></span>
        </Link>

        <div style={{ position:"relative", zIndex:1, marginTop: 48, marginBottom: 48 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:".14em", color:amberD, textTransform:"uppercase", marginBottom:14 }}>Driver Portal</div>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(28px,3.5vw,38px)", fontWeight:900, color:"#fff", lineHeight:1.15, marginBottom:18 }}>
            The Platform<br/>Built for Pilot<br/><span style={{ color:amberD }}>Car Pros.</span>
          </h1>
          <p style={{ fontSize:14, color:"rgba(255,255,255,0.60)", lineHeight:1.8, maxWidth:300 }}>
            Log in to access your load board, training progress, equipment listings, and verified driver profile.
          </p>
        </div>

        <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:12, marginBottom:44 }}>
          {[
            { icon:"📋", text:"Apply to oversize loads in 1 click" },
            { icon:"🏅", text:"Verified driver badge on your profile" },
            { icon:"🛒", text:"Buy & sell pilot car equipment" },
            { icon:"📄", text:"Send invoices directly to companies" },
          ].map(f => (
            <div key={f.text} style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:34, height:34, borderRadius:9, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, flexShrink:0 }}>{f.icon}</div>
              <span style={{ fontSize:13, color:"rgba(255,255,255,0.75)", fontWeight:500 }}>{f.text}</span>
            </div>
          ))}
        </div>

        <div style={{ position:"relative", zIndex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
          {[["12,000+","Certified Drivers"],["50","States Covered"],["$2.4M","Paid Out Monthly"],["4.9★","Avg Rating"]].map(([n,l]) => (
            <div key={l} style={{ padding:"14px 16px", borderRadius:12, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.10)" }}>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:20, fontWeight:800, color:amberD }}>{n}</div>
              <div style={{ fontSize:10.5, color:"rgba(255,255,255,0.50)", marginTop:2, fontWeight:500 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── RIGHT PANEL — login form ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:"100vh" }}>
        
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px 0" }}>
          <Link to="/" style={{ display:"flex", alignItems:"center", gap:7, textDecoration:"none", color:textMid, fontSize:13, fontWeight:600 }}>
            <ArrowLeft size={15} />
            <span className="hidden sm:inline">Back to Home</span>
          </Link>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:12, color:textSoft }}>No account?</span>
            <Link to="/signup" style={{ fontSize:13, fontWeight:700, color:amberBtn, textDecoration:"none", padding:"7px 16px", borderRadius:99, border:`1.5px solid ${amberBtn}`, transition:"all .15s" }}>
              Join Free →
            </Link>
            <button onClick={() => setTheme(D ? "light" : "dark")} aria-label="Toggle theme"
              style={{ width:36, height:36, borderRadius:9, border:`1px solid ${border}`, background:inputBg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:textMid }}>
              {D ? <Sun size={15} /> : <Moon size={15} />}
            </button>
          </div>
        </div>

        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 24px" }}>
          <div style={{ width:"100%", maxWidth:420 }}>

            <div className="lg:hidden" style={{ display:"flex", alignItems:"center", gap:9, marginBottom:28 }}>
              <div style={{ width:36, height:36, borderRadius:9, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:16, color:"#0d1f3c" }}>P</div>
              <span style={{ fontSize:17, fontWeight:800, color:text }}>Pilot<span style={{ color:amber }}>Link</span> <span style={{ fontSize:11, color:textSoft, fontWeight:500 }}>USA</span></span>
            </div>

            <div style={{ marginBottom:28 }}>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:900, color:text, marginBottom:6, lineHeight:1.2 }}>Welcome back, Driver</h2>
              <p style={{ fontSize:14, color:textMid, lineHeight:1.6 }}>Sign in to your PilotLink driver account.</p>
            </div>

            {reason === "loadboard" && (
              <div style={{ marginBottom:20, padding:"12px 16px", borderRadius:12, border:`1px solid ${D?"rgba(251,191,36,0.25)":"#fde68a"}`, background:D?"rgba(251,191,36,0.08)":"#fffbeb", display:"flex", gap:10, alignItems:"flex-start" }}>
                <span style={{ fontSize:18, flexShrink:0 }}>🚛</span>
                <div>
                  <p style={{ fontSize:13, fontWeight:700, color:D?"#fbbf24":"#92400e", margin:0, marginBottom:3 }}>Driver login required</p>
                  <p style={{ fontSize:12, color:D?"rgba(251,191,36,0.70)":"#b45309", margin:0 }}>
                    Log in to browse and apply for open loads.{" "}
                    <Link to="/signup" style={{ fontWeight:700, color:amberBtn, textDecoration:"underline" }}>Sign up free →</Link>
                  </p>
                </div>
              </div>
            )}

            {apiError && (
              <div style={{ marginBottom:16, padding:"11px 14px", borderRadius:10, background:D?"rgba(251,113,133,0.10)":"#fff1f2", border:`1px solid ${D?"rgba(251,113,133,0.25)":"#fecdd3"}`, fontSize:13, color:D?"#fda4af":"#be123c", fontWeight:500 }}>
                ⚠ {apiError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div style={{ marginBottom:16 }}>
                <label style={{ display:"block", fontSize:12, fontWeight:700, color:textMid, marginBottom:7, letterSpacing:".03em" }}>Email Address</label>
                <div style={{ position:"relative" }}>
                  <Mail size={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" }} />
                  <input type="email" value={email} autoComplete="email" placeholder="you@example.com"
                    onChange={e => { setEmail(e.target.value); setApiError(""); }}
                    onFocus={e => e.target.style.borderColor=inputFocus}
                    onBlur={e  => e.target.style.borderColor=border}
                    style={inputClass} required />
                </div>
              </div>

              <div style={{ marginBottom:22 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
                  <label style={{ fontSize:12, fontWeight:700, color:textMid, letterSpacing:".03em" }}>Password</label>
                  <Link to="/forgot-password" style={{ fontSize:12, fontWeight:600, color:amberBtn, textDecoration:"none" }}>Forgot password?</Link>
                </div>
                <div style={{ position:"relative" }}>
                  <Lock size={16} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" }} />
                  <input type={showPw?"text":"password"} value={password} autoComplete="current-password" placeholder="••••••••"
                    onChange={e => { setPassword(e.target.value); setApiError(""); }}
                    onFocus={e => e.target.style.borderColor=inputFocus}
                    onBlur={e  => e.target.style.borderColor=border}
                    style={{ ...inputClass, paddingRight:44 }} required />
                  <button type="button" onClick={() => setShowPw(p=>!p)}
                    style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:textSoft, display:"flex" }}>
                    {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>

              <button type="submit" disabled={isLoading} style={{
                width:"100%", padding:"14px", borderRadius:12, border:"none", cursor:isLoading?"not-allowed":"pointer",
                background:isLoading?"rgba(245,158,11,0.60)":`linear-gradient(135deg,${amber},#fbbf24)`,
                color:"#0d1f3c", fontSize:15, fontWeight:800, display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                boxShadow:isLoading?"none":"0 4px 18px rgba(245,158,11,0.40)", transition:"all .15s",
                fontFamily:"inherit",
              }}
              onMouseEnter={e=>{ if(!isLoading) e.currentTarget.style.filter="brightness(1.06)"; }}
              onMouseLeave={e=>{ e.currentTarget.style.filter="none"; }}>
                {isLoading
                  ? <><Loader2 size={18} style={{ animation:"spin 1s linear infinite" }} /> Signing you in…</>
                  : <>Sign In <ChevronRight size={18}/></>}
              </button>
            </form>

            <div style={{ display:"flex", justifyContent:"center", alignItems:"center", gap:6, marginTop:16, fontSize:12, color:textSoft }}>
              <ShieldCheck size={13} style={{ color:"#22c55e" }} />
              Secured with 256-bit TLS encryption
            </div>

            <div style={{ display:"flex", alignItems:"center", gap:12, margin:"24px 0 20px" }}>
              <div style={{ flex:1, height:1, background:border }} />
              <span style={{ fontSize:11, color:textSoft, fontWeight:600, whiteSpace:"nowrap" }}>Other login options</span>
              <div style={{ flex:1, height:1, background:border }} />
            </div>

            <p style={{ textAlign:"center", marginTop:24, fontSize:13, color:textMid }}>
              New to PilotLink?{" "}
              <Link to="/signup" style={{ fontWeight:800, color:amberBtn, textDecoration:"none" }}>Create a free account →</Link>
            </p>

            <div style={{ display:"flex", justifyContent:"center", gap:18, marginTop:22, flexWrap:"wrap" }}>
              {TRUST.map(t => (
                <div key={t.label} style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, color:textSoft }}>
                  <span>{t.icon}</span>{t.label}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ padding:"16px 24px", textAlign:"center", fontSize:12, color:textSoft, borderTop:`1px solid ${border}` }}>
          © {new Date().getFullYear()} PilotLink USA. All rights reserved.
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@700;800;900&family=Inter:wght@400;500;600;700;800&display=swap');
      `}</style>
    </div>
  );
}